import type { EditableElement } from '../editing/types';
import type {
  HowMightWeData,
  ProblemStatementsData,
  InterviewQuestionsData,
  UserStoriesData,
  SurveyQuestionsData,
  FiveWhysData,
  PersonaData,
  JourneyMapData,
  MindMapData,
} from '../types/visualizations';
import { COLORS, SIZES, SPACING, SHADOWS, FONTS, STICKY_COLORS } from '../utils/constants';
import { calculateStickyHeight, estimateTextHeight } from './helpers';
import { getTextDirection } from '../utils/rtlText';

/**
 * Converts HowMightWe data to editable elements
 * Matches the visual structure from HowMightWeRenderer:
 * - Orange section wrapper with title
 * - White section box inside
 * - Yellow sticky notes with auto-calculated heights
 */
export function convertHowMightWeToElements(data: HowMightWeData): EditableElement[] {
  const elements: EditableElement[] = [];
  const stickyWidth = SIZES.STICKY_NOTE_WIDTH;
  const maxColumns = 3;
  const spacingX = SPACING.STICKY_GAP;
  const spacingY = SPACING.STICKY_GAP;
  const outerPadding = SPACING.SECTION_PADDING;
  const stickyPadding = SPACING.STICKY_GAP;
  const titleHeight = 50;
  const titleSpacing = 30;

  // Calculate positions and heights for all sticky notes (relative to white section origin)
  const stickyPositions: { x: number; y: number; width: number; height: number }[] = [];
  const minHeight = 100;
  
  data.questions.forEach((question, index) => {
    const col = index % maxColumns;
    const row = Math.floor(index / maxColumns);
    const height = calculateStickyHeight(question, stickyWidth, 14, 12);
    
    // Position relative to white section box (will be offset by white section position later)
    const x = stickyPadding + col * (stickyWidth + spacingX);
    let y = stickyPadding;
    
    // Calculate Y based on previous rows
    for (let r = 0; r < row; r++) {
      let rowMaxHeight = minHeight;
      for (let c = 0; c < maxColumns; c++) {
        const idx = r * maxColumns + c;
        if (idx < stickyPositions.length) {
          rowMaxHeight = Math.max(rowMaxHeight, stickyPositions[idx].height);
        }
      }
      y += rowMaxHeight + spacingY;
    }
    
    stickyPositions.push({ x, y, width: stickyWidth, height });
  });

  // Calculate dimensions
  const maxX = Math.max(...stickyPositions.map(p => p.x + p.width));
  let maxY = titleHeight + titleSpacing + outerPadding + stickyPadding;
  const rows = Math.ceil(data.questions.length / maxColumns);
  
  for (let r = 0; r < rows; r++) {
    let rowMaxHeight = minHeight;
    for (let c = 0; c < maxColumns; c++) {
      const idx = r * maxColumns + c;
      if (idx < stickyPositions.length) {
        rowMaxHeight = Math.max(rowMaxHeight, stickyPositions[idx].height);
      }
    }
    maxY += rowMaxHeight;
    if (r < rows - 1) maxY += spacingY;
  }
  maxY += stickyPadding + outerPadding;

  const whiteSectionWidth = maxX + outerPadding + stickyPadding;
  const whiteSectionHeight = maxY - titleHeight - titleSpacing;
  const orangeSectionWidth = whiteSectionWidth + 80;
  const orangeSectionHeight = whiteSectionHeight + titleHeight + titleSpacing ;

  // 1. Organizer (Orange section wrapper with title - matches SectionWrapper)
  elements.push({
    id: `hmw-organizer-${Date.now()}`,
    type: 'organizer',
    x: 0,
    y: 0,
    width: orangeSectionWidth,
    height: orangeSectionHeight,
    rotation: 0,
    data: {
      title: 'How Might We',
    },
  });

  // 2. Section (White section box - matches WhiteSectionBox)
  elements.push({
    id: `hmw-section-${Date.now()}`,
    type: 'section',
    x: outerPadding,
    y: outerPadding,
    width: whiteSectionWidth,
    height: whiteSectionHeight,
    rotation: 0,
    data: {},
  });

  // 3. Yellow sticky notes with questions (matches StickyNote component)
  data.questions.forEach((question, index) => {
    elements.push({
      id: `hmw-sticky-${index}-${Date.now()}`,
      type: 'sticky',
      // Position relative to white section box position (outerPadding, outerPadding)
      x: outerPadding + stickyPositions[index].x,
      y: outerPadding + stickyPositions[index].y,
      width: stickyPositions[index].width,
      height: stickyPositions[index].height,
      rotation: 0,
      data: {
        text: question,
        backgroundColor: COLORS.YELLOW,
        fontSize: 14,
        fontFamily: FONTS.FAMILY,
        fill: COLORS.BLACK,
        align: 'left',
        padding: SPACING.STICKY_PADDING,
        shadowColor: SHADOWS.STICKY_NOTE.color,
        shadowBlur: SHADOWS.STICKY_NOTE.blur,
        shadowOffsetX: SHADOWS.STICKY_NOTE.offset.x,
        shadowOffsetY: SHADOWS.STICKY_NOTE.offset.y,
        shadowOpacity: SHADOWS.STICKY_NOTE.opacity,
        cornerRadius: SIZES.STICKY_NOTE_CORNER_RADIUS,
      },
    });
  });

  return elements;
}

/**
 * Converts Problem Statements data to editable elements
 * Matches the visual structure from ProblemStatementsRenderer:
 * - Organizer (SectionWrapper) with title
 * - Multiple section groups (WhiteSectionBox) with titles/subtitles
 * - Sticky notes with rotating colors, arranged in 3-column grid
 * - Dynamic heights based on content
 */
export function convertProblemStatementsToElements(data: ProblemStatementsData): EditableElement[] {
  const elements: EditableElement[] = [];
  const { statements = [], dir } = data;
  
  if (!statements || statements.length === 0) {
    return elements;
  }

  // Determine text direction
  const direction = dir || getTextDirection(statements[0]?.subtitle || statements[0]?.title || '');
  const titleText = direction === 'rtl' ? 'مخطط لوصف المشكلة' : 'Problem Statement Structure';

  // Group statements by section (title is the section name)
  const sectionGroups: { title: string; subtitle?: string; notes: string[] }[] = [];
  const groupMap: { [key: string]: { title: string; subtitle?: string; notes: string[] } } = {};
  
  statements.forEach((statement) => {
    const sectionName = statement.title || 'Other';
    if (!groupMap[sectionName]) {
      groupMap[sectionName] = {
        title: sectionName,
        subtitle: statement.subtitle,
        notes: [],
      };
      sectionGroups.push(groupMap[sectionName]);
    }
    // Each sticky note content (subtitle + description)
    const noteContent = [statement.subtitle, statement.description].filter(Boolean).join('\n\n');
    groupMap[sectionName].notes.push(noteContent);
  });

  // Layout configuration matching ProblemStatementsRenderer
  const wrapperPadding = 10;
  const additionalTopSpace = 10;
  const additionalLeftSpace = 10;
  const betweenSpacing = 20;
  const maxWidth = 1200;
  const baseWidth = maxWidth;

  // Section configuration
  const outerPadding = 20;
  const stickyPadding = 20;
  const spacingX = 20;
  const spacingY = 20;
  const titleSpacing = 30;
  const maxColumns = 3;
  const titleFontSize = 24;
  const subtitleFontSize = 16;
  const titleLineHeight = titleFontSize * 1.4;
  const subtitleLineHeight = subtitleFontSize * 1.4;
  const stickyWidth = SIZES.STICKY_NOTE_WIDTH;
  const minHeight = 100;

  // Calculate section dimensions and positions
  const sectionDimensions: Array<{
    width: number;
    height: number;
    stickyPositions: Array<{ x: number; y: number; height: number; text: string }>;
    title: string;
    subtitle?: string;
    color: string;
  }> = [];

  sectionGroups.forEach((group, groupIndex) => {
    const sectionColor = STICKY_COLORS[groupIndex % STICKY_COLORS.length];
    const flatTexts = group.notes;
    const stickyPositions: Array<{ x: number; y: number; height: number; text: string }> = [];

    // Calculate title/subtitle height
    const titleY = outerPadding;
    let subtitleY = titleY + titleLineHeight + 4;
    let contentStartY = subtitleY;
    
    if (group.subtitle) {
      contentStartY = subtitleY + subtitleLineHeight + titleSpacing;
    } else {
      contentStartY = titleY + titleLineHeight + titleSpacing;
    }

    // Calculate sticky note positions and heights
    flatTexts.forEach((text, index) => {
      const col = index % maxColumns;
      const row = Math.floor(index / maxColumns);
      const height = calculateStickyHeight(text, stickyWidth, 14, SPACING.STICKY_PADDING);
      
      const stickyX = direction === 'rtl'
        ? outerPadding + stickyPadding + (maxColumns - 1 - col) * (stickyWidth + spacingX)
        : outerPadding + stickyPadding + col * (stickyWidth + spacingX);
      
      // Calculate Y position based on previous rows
      let stickyY = contentStartY + stickyPadding;
      for (let r = 0; r < row; r++) {
        let rowMaxHeight = minHeight;
        for (let c = 0; c < maxColumns; c++) {
          const idx = r * maxColumns + c;
          if (idx < stickyPositions.length) {
            rowMaxHeight = Math.max(rowMaxHeight, stickyPositions[idx].height);
          }
        }
        stickyY += rowMaxHeight + spacingY;
      }

      stickyPositions.push({ x: stickyX, y: stickyY, height, text });
    });

    // Calculate total section height
    let totalStickyHeight = contentStartY + stickyPadding;
    if (flatTexts.length > 0) {
      const rows = Math.ceil(flatTexts.length / maxColumns);
      for (let r = 0; r < rows; r++) {
        let rowMaxHeight = minHeight;
        for (let c = 0; c < maxColumns; c++) {
          const idx = r * maxColumns + c;
          if (idx < stickyPositions.length) {
            rowMaxHeight = Math.max(rowMaxHeight, stickyPositions[idx].height);
          }
        }
        totalStickyHeight += rowMaxHeight;
        if (r < rows - 1) {
          totalStickyHeight += spacingY;
        }
      }
      totalStickyHeight += stickyPadding + outerPadding;
    } else {
      totalStickyHeight += outerPadding;
    }

    // Calculate section width
    const maxRowWidth = Math.max(
      ...stickyPositions.map(p => p.x + stickyWidth),
      outerPadding + stickyPadding
    );
    const sectionWidth = Math.max(maxRowWidth + outerPadding + stickyPadding, 400);
    const sectionHeight = totalStickyHeight;

    sectionDimensions.push({
      width: sectionWidth,
      height: sectionHeight,
      stickyPositions,
      title: group.title,
      subtitle: group.subtitle,
      color: sectionColor,
    });
  });

  // Calculate flow layout for sections
  let currentRowHeight = 0;
  let currentY = additionalTopSpace;
  let currentX = additionalLeftSpace;
  let maxX = 0;
  let maxY = 0;
  const sectionPadding = 20;

  const sectionLayouts: Array<{ x: number; y: number; sectionIndex: number }> = [];

  sectionDimensions.forEach((section, sectionIndex) => {
    const sectionWidthWithPadding = section.width + sectionPadding * 2;
    
    // Check if section fits in current row
    if (currentX + sectionWidthWithPadding > baseWidth) {
      currentX = additionalLeftSpace;
      currentY += currentRowHeight + betweenSpacing;
      currentRowHeight = 0;
    }

    const sectionX = direction === 'rtl'
      ? baseWidth - currentX - sectionWidthWithPadding
      : currentX;
    const sectionY = currentY;

    sectionLayouts.push({ x: sectionX, y: sectionY, sectionIndex });

    currentX += sectionWidthWithPadding + betweenSpacing;
    currentRowHeight = Math.max(currentRowHeight, section.height + sectionPadding * 2);
    maxX = Math.max(maxX, sectionX + sectionWidthWithPadding);
    maxY = Math.max(maxY, sectionY + section.height + sectionPadding * 2);
  });

  const contentWidth = maxX + additionalLeftSpace;
  const contentHeight = maxY + wrapperPadding;

  // 1. Organizer (SectionWrapper equivalent)
  elements.push({
    id: `ps-organizer-${Date.now()}`,
    type: 'organizer',
    x: 0,
    y: 0,
    width: contentWidth,
    height: contentHeight,
    rotation: 0,
    data: {
      title: titleText,
    },
  });

  // 2. Create sections and sticky notes
  sectionLayouts.forEach((layout) => {
    const section = sectionDimensions[layout.sectionIndex];
    const sectionX = layout.x + sectionPadding;
    const sectionY = layout.y + sectionPadding;

    // Section element (WhiteSectionBox equivalent)
    elements.push({
      id: `ps-section-${layout.sectionIndex}-${Date.now()}`,
      type: 'section',
      x: sectionX,
      y: sectionY,
      width: section.width,
      height: section.height,
      rotation: 0,
      data: {},
    });

    // Section title
    elements.push({
      id: `ps-section-title-${layout.sectionIndex}-${Date.now()}`,
      type: 'text',
      x: sectionX + outerPadding,
      y: sectionY + outerPadding,
      width: section.width - outerPadding * 2,
      height: titleLineHeight,
      rotation: 0,
      data: {
        text: section.title,
        fontSize: titleFontSize,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.SEMI_BOLD,
        fill: COLORS.BLACK,
        align: direction === 'rtl' ? 'right' : 'left',
      },
    });

    // Section subtitle (if exists)
    if (section.subtitle) {
      const subtitleY = sectionY + outerPadding + titleLineHeight + 4;
      elements.push({
        id: `ps-section-subtitle-${layout.sectionIndex}-${Date.now()}`,
        type: 'text',
        x: sectionX + outerPadding,
        y: subtitleY,
        width: section.width - outerPadding * 2,
        height: subtitleLineHeight,
        rotation: 0,
        data: {
          text: section.subtitle,
          fontSize: subtitleFontSize,
          fontFamily: FONTS.FAMILY,
          fontWeight: FONTS.WEIGHTS.REGULAR,
          fill: COLORS.BLACK,
          align: direction === 'rtl' ? 'right' : 'left',
        },
      });
    }

    // Sticky notes
    section.stickyPositions.forEach((pos, stickyIndex) => {
      elements.push({
        id: `ps-sticky-${layout.sectionIndex}-${stickyIndex}-${Date.now()}`,
      type: 'sticky',
        x: sectionX + pos.x,
        y: sectionY + pos.y,
        width: stickyWidth,
        height: pos.height,
      rotation: 0,
      data: {
          text: pos.text,
          backgroundColor: section.color,
        fontSize: 14,
          fontFamily: FONTS.FAMILY,
          fill: COLORS.BLACK,
          align: direction === 'rtl' ? 'right' : 'left',
          padding: SPACING.STICKY_PADDING,
          shadowColor: SHADOWS.STICKY_NOTE.color,
          shadowBlur: SHADOWS.STICKY_NOTE.blur,
          shadowOffsetX: SHADOWS.STICKY_NOTE.offset.x,
          shadowOffsetY: SHADOWS.STICKY_NOTE.offset.y,
          shadowOpacity: SHADOWS.STICKY_NOTE.opacity,
          cornerRadius: SIZES.STICKY_NOTE_CORNER_RADIUS,
        },
      });
    });
  });

  return elements;
}

/**
 * Converts Interview Questions data to editable elements
 * Matches the visual structure from InterviewQuestionsRenderer:
 * - Organizer (SectionWrapper) with title
 * - White section box containing title, description, and questions
 * - Text elements for questions and follow-ups
 * - Optional focus areas section with green background
 */
export function convertInterviewQuestionsToElements(data: InterviewQuestionsData): EditableElement[] {
  const elements: EditableElement[] = [];
  const { title = 'Interview Questions', description = '', questions, focus_areas = [], dir } = data;
  
  if (!questions || questions.length === 0) {
    return elements;
  }

  // Determine text direction
  const direction = dir || getTextDirection(questions[0]?.question || '');
  
  // Layout configuration matching InterviewQuestionsRenderer
  const interviewWidth = 1200;
  const interviewPadding = 50;
  const contentWidth = interviewWidth - 2 * interviewPadding;
  const wrapperPadding = 40;
  
  const titleFontSize = 32;
  const descriptionFontSize = 24;
  const questionFontSize = 18;
  const followUpFontSize = 16;
  const focusTitleFontSize = 24;
  const focusItemFontSize = 16;
  const questionSpacing = 15;
  
  // Calculate white section content positions and heights
  let currentY = interviewPadding;
  
  // Title measurement
  const titleHeight = estimateTextHeight(title, contentWidth, titleFontSize, 1.4);
  const titleY = currentY;
  currentY += titleHeight + 40;
  
  // Description measurement
  let descriptionY = 0;
  let descriptionHeight = 0;
  if (description) {
    descriptionY = currentY;
    descriptionHeight = estimateTextHeight(description, contentWidth, descriptionFontSize, 1.4);
    currentY += descriptionHeight + questionSpacing;
  }
  
  // Calculate questions positions and heights
  const questionItems: Array<{
    questionY: number;
    questionHeight: number;
    followUpY: number;
    followUpHeight: number;
    totalHeight: number;
  }> = [];
  
  questions.forEach((q, index) => {
    const questionY = currentY;
    const questionText = `${index + 1}. ${q.question}`;
    const questionHeight = estimateTextHeight(questionText, contentWidth, questionFontSize, 1.4);
    
    let followUpHeight = 0;
    let followUpY = 0;
    if (q.follow_up && q.follow_up.length > 0) {
      followUpY = questionY + questionHeight + 10;
      const followUpText = `FollowUp: ${q.follow_up.join(', ')}`;
      followUpHeight = estimateTextHeight(followUpText, contentWidth - 38, followUpFontSize, 1.4);
    }
    
    const totalQuestionHeight = questionHeight + (followUpHeight > 0 ? 30 + followUpHeight : 0);
    questionItems.push({
      questionY,
      questionHeight,
      followUpY,
      followUpHeight,
      totalHeight: totalQuestionHeight,
    });
    
    currentY += totalQuestionHeight + questionSpacing;
  });
  
  const whiteSectionHeight = currentY - questionSpacing + interviewPadding;
  const whiteSectionWidth = interviewWidth - wrapperPadding;
  
  // Calculate focus areas section if it exists
  let focusAreasHeight = 0;
  let focusAreasY = 0;
  const focusAreasItems: Array<{ y: number; title?: string; description: string; height: number }> = [];
  
  if (focus_areas && focus_areas.length > 0) {
    focusAreasY = whiteSectionHeight + wrapperPadding / 2;
    let focusY = focusAreasY + 100;
    
    const focusTitle = direction === 'ltr' 
      ? 'Key Focus Areas for Qualitative Insights:' 
      : 'مجالات التركيز الرئيسية للحصول على رؤى نوعية:';
    
    const focusTitleHeight = estimateTextHeight(focusTitle, contentWidth, focusTitleFontSize, 1.4);
    focusY += focusTitleHeight + 50;
    
    focus_areas.forEach((area: any) => {
      const areaTitle = typeof area === 'object' ? area.title : '';
      const areaDescription = typeof area === 'string' ? area : (area.description || '');
      
      const itemHeight = estimateTextHeight(
        areaDescription,
        contentWidth - (areaTitle ? 200 : 0),
        focusItemFontSize,
        1.4
      );
      
      focusAreasItems.push({
        y: focusY,
        title: areaTitle,
        description: areaDescription,
        height: itemHeight,
      });
      
      focusY += itemHeight + 10;
    });
    
    focusAreasHeight = focusY - focusAreasY + 100;
  }
  
  const totalContentWidth = interviewWidth;
  const totalContentHeight = whiteSectionHeight + focusAreasHeight + wrapperPadding;
  
  // 1. Organizer (SectionWrapper equivalent)
  elements.push({
    id: `iq-organizer-${Date.now()}`,
    type: 'organizer',
    x: 0,
    y: 0,
    width: totalContentWidth,
    height: totalContentHeight,
    rotation: 0,
    data: {
      title: title,
    },
  });
  
  // 2. Section (WhiteSectionBox equivalent)
  elements.push({
    id: `iq-section-${Date.now()}`,
    type: 'section',
    x: wrapperPadding / 2,
    y: wrapperPadding / 2,
    width: whiteSectionWidth,
    height: whiteSectionHeight,
    rotation: 0,
    data: {},
  });
  
  // 3. Title text
  elements.push({
    id: `iq-title-${Date.now()}`,
    type: 'text',
    x: interviewPadding + wrapperPadding / 2,
    y: titleY + wrapperPadding / 2,
    width: contentWidth,
    height: titleHeight,
    rotation: 0,
    data: {
      text: title,
      fontSize: titleFontSize,
      fontFamily: FONTS.FAMILY,
      fontWeight: FONTS.WEIGHTS.SEMI_BOLD,
      fill: COLORS.BLACK,
      align: direction === 'rtl' ? 'right' : 'left',
    },
  });

  // 4. Description text
  if (description) {
    elements.push({
      id: `iq-desc-${Date.now()}`,
      type: 'text',
      x: interviewPadding + wrapperPadding / 2,
      y: descriptionY + wrapperPadding / 2,
      width: contentWidth,
      height: descriptionHeight,
      rotation: 0,
      data: {
        text: description,
        fontSize: descriptionFontSize,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.REGULAR,
        fill: COLORS.BLACK,
        align: direction === 'rtl' ? 'right' : 'left',
      },
    });
  }
  
  // 5. Questions and follow-ups
  questions.forEach((question, qIndex) => {
    const item = questionItems[qIndex];
    const questionText = `${qIndex + 1}. ${question.question}`;
    
    // Question text
    elements.push({
      id: `iq-question-${qIndex}-${Date.now()}`,
      type: 'text',
      x: interviewPadding + wrapperPadding / 2,
      y: item.questionY + wrapperPadding / 2,
      width: contentWidth,
      height: item.questionHeight,
      rotation: 0,
      data: {
        text: questionText,
        fontSize: questionFontSize,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.SEMI_BOLD,
        fill: COLORS.BLACK,
        align: direction === 'rtl' ? 'right' : 'left',
      },
    });
    
    // Follow-up text
    if (question.follow_up && question.follow_up.length > 0 && item.followUpHeight > 0) {
      elements.push({
        id: `iq-followup-${qIndex}-${Date.now()}`,
        type: 'text',
        x: interviewPadding + wrapperPadding / 2 + 38,
        y: item.followUpY + wrapperPadding / 2,
        width: contentWidth - 38,
        height: item.followUpHeight,
        rotation: 0,
        data: {
          text: `FollowUp: ${question.follow_up.join(', ')}`,
          fontSize: followUpFontSize,
          fontFamily: FONTS.FAMILY,
          fontWeight: FONTS.WEIGHTS.REGULAR,
          fill: COLORS.BLACK,
          align: direction === 'rtl' ? 'right' : 'left',
        },
      });
    }
  });
  
  // 6. Focus Areas section (if exists)
  if (focus_areas && focus_areas.length > 0) {
    // Green background rectangle
    elements.push({
      id: `iq-focus-bg-${Date.now()}`,
      type: 'shape',
      x: wrapperPadding / 2,
      y: focusAreasY,
      width: interviewWidth - wrapperPadding,
      height: focusAreasHeight,
      rotation: 0,
      data: {
        shapeType: 'rectangle',
        fill: '#F1FFF1',
        backgroundColor: '#F1FFF1',
        cornerRadius: 8,
        stroke: 'transparent',
        strokeWidth: 0,
      },
    });
    
    // Focus areas title
    const focusTitle = direction === 'ltr' 
      ? 'Key Focus Areas for Qualitative Insights:' 
      : 'مجالات التركيز الرئيسية للحصول على رؤى نوعية:';
    
    const focusTitleHeight = estimateTextHeight(focusTitle, contentWidth, focusTitleFontSize, 1.4);

    elements.push({
      id: `iq-focus-title-${Date.now()}`,
      type: 'text',
      x: interviewPadding + wrapperPadding / 2,
      y: focusAreasY + 100,
      width: contentWidth,
      height: focusTitleHeight,
      rotation: 0,
      data: {
        text: focusTitle,
        fontSize: focusTitleFontSize,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.SEMI_BOLD,
        fill: COLORS.BLACK,
        align: direction === 'rtl' ? 'right' : 'left',
      },
    });

    // Focus areas items
    focusAreasItems.forEach((item, index) => {
      if (item.title) {
        elements.push({
          id: `iq-focus-title-${index}-${Date.now()}`,
          type: 'text',
          x: interviewPadding + wrapperPadding / 2,
          y: item.y,
          width: 200,
          height: item.height,
          rotation: 0,
          data: {
            text: `${item.title}:`,
            fontSize: focusItemFontSize,
            fontFamily: FONTS.FAMILY,
            fontWeight: FONTS.WEIGHTS.SEMI_BOLD,
            fill: COLORS.BLACK,
            align: direction === 'rtl' ? 'right' : 'left',
          },
        });
      }
      
      elements.push({
        id: `iq-focus-item-${index}-${Date.now()}`,
        type: 'text',
        x: interviewPadding + wrapperPadding / 2 + (item.title ? 200 : 0),
        y: item.y,
        width: contentWidth - (item.title ? 200 : 0),
        height: item.height,
        rotation: 0,
        data: {
          text: item.description,
          fontSize: focusItemFontSize,
          fontFamily: FONTS.FAMILY,
          fontWeight: FONTS.WEIGHTS.REGULAR,
          fill: COLORS.BLACK,
          align: direction === 'rtl' ? 'right' : 'left',
        },
      });
    });
  }

  return elements;
}

/**
 * Converts User Stories data to editable elements
 * Matches the visual structure from UserStoriesRenderer:
 * - Organizer (SectionWrapper) with title
 * - Template text at the top
 * - White section box containing yellow sticky notes (cards) in a horizontal row
 * - Dynamic heights for sticky notes based on content
 */
export function convertUserStoriesToElements(data: UserStoriesData): EditableElement[] {
  const elements: EditableElement[] = [];
  const { title, stories, dir } = data;
  
  if (!stories || stories.length === 0) {
    return elements;
  }

  // Determine text direction
  const direction = dir || getTextDirection(title);
  
  // Layout configuration matching UserStoriesRenderer
  const sectionPadding = 100;
  const whitePadding = 40;
  const cardSpacing = 24;
  const titleSpacing = 60;
  const cardWidth = 420;
  const maxWidth = 1400;
  const templateFontSize = 20;
  
  const templateText = "As a [user type], I want to [do something], so that [I achieve this goal/value].";
  
  // Measure template text
  const templateHeight = estimateTextHeight(
    templateText,
    maxWidth - sectionPadding * 2,
    templateFontSize,
    1.4
  );
  
  // Prepare story texts with acceptance criteria
  const storyTexts = stories.map((story) => {
    let fullText = story.story;
    
    if (story.acceptance_criteria && story.acceptance_criteria.length > 0) {
      fullText += '\n\n✅ Acceptance:\n';
      fullText += story.acceptance_criteria.join('\n');
    }
    
    return fullText;
  });
  
  // Calculate sticky note heights dynamically
  const stickyHeights = storyTexts.map((text) => 
    calculateStickyHeight(text, cardWidth, 18, SPACING.STICKY_PADDING)
  );
  
  // Find the tallest card for white section height
  const maxCardHeight = Math.max(...stickyHeights, 400); // Minimum 400 as in renderer
  const whiteHeight = maxCardHeight + whitePadding * 2;
  
  // Calculate white section width (all cards in one row)
  const cardsPerRow = stories.length;
  const whiteWidth = cardsPerRow * cardWidth + (cardsPerRow - 1) * cardSpacing + whitePadding * 2;
  
  // Calculate total dimensions
  const totalContentWidth = whiteWidth + sectionPadding * 2;
  const totalContentHeight = sectionPadding + templateHeight + titleSpacing + whiteHeight + sectionPadding;
  
  // 1. Organizer (SectionWrapper equivalent)
  elements.push({
    id: `us-organizer-${Date.now()}`,
    type: 'organizer',
    x: 0,
    y: 0,
    width: totalContentWidth,
    height: totalContentHeight,
    rotation: 0,
    data: {
      title: title,
    },
  });
  
  // 2. Template text
  elements.push({
    id: `us-template-${Date.now()}`,
    type: 'text',
    x: sectionPadding,
    y: sectionPadding,
    width: maxWidth - sectionPadding * 2,
    height: templateHeight,
    rotation: 0,
    data: {
      text: templateText,
      fontSize: templateFontSize,
      fontFamily: FONTS.FAMILY,
      fontWeight: FONTS.WEIGHTS.REGULAR,
      fill: COLORS.BLACK,
      align: direction === 'rtl' ? 'right' : 'left',
    },
  });

  // 3. Section (WhiteSectionBox equivalent)
  const sectionX = sectionPadding;
  const sectionY = sectionPadding + templateHeight + titleSpacing;
  
  elements.push({
    id: `us-section-${Date.now()}`,
    type: 'section',
    x: sectionX,
    y: sectionY,
    width: whiteWidth,
    height: whiteHeight,
    rotation: 0,
    data: {},
  });
  
  // 4. Story cards as sticky notes (arranged horizontally)
  storyTexts.forEach((text, index) => {
    const cardX = sectionX + whitePadding + index * (cardWidth + cardSpacing);
    const cardY = sectionY + whitePadding;

    elements.push({
      id: `us-sticky-${index}-${Date.now()}`,
      type: 'sticky',
      x: cardX,
      y: cardY,
      width: cardWidth,
      height: stickyHeights[index],
      rotation: 0,
      data: {
        text: text,
        backgroundColor: COLORS.YELLOW,
        fontSize: 18,
        fontFamily: FONTS.FAMILY,
        fill: COLORS.BLACK,
        align: direction === 'rtl' ? 'right' : 'left',
        padding: SPACING.STICKY_PADDING,
        shadowColor: SHADOWS.STICKY_NOTE.color,
        shadowBlur: SHADOWS.STICKY_NOTE.blur,
        shadowOffsetX: SHADOWS.STICKY_NOTE.offset.x,
        shadowOffsetY: SHADOWS.STICKY_NOTE.offset.y,
        shadowOpacity: SHADOWS.STICKY_NOTE.opacity,
        cornerRadius: SIZES.STICKY_NOTE_CORNER_RADIUS,
      },
    });
  });

  return elements;
}

/**
 * Converts Survey Questions data to editable elements
 * Matches the visual structure from SurveyQuestionsRenderer:
 * - Organizer (SectionWrapper) with title
 * - White section box containing title, description, questions, and answers
 * - Text elements for all content with dynamic heights
 */
export function convertSurveyQuestionsToElements(data: SurveyQuestionsData): EditableElement[] {
  const elements: EditableElement[] = [];
  const { title = 'Survey Questions', description, questions, dir } = data;
  
  if (!questions || questions.length === 0) {
    return elements;
  }

  // Determine text direction
  const direction = dir || getTextDirection(questions[0]?.question || '');
  
  // Layout configuration matching SurveyQuestionsRenderer
  const sectionPadding = 40;
  const whitePadding = 40;
  const maxWidth = 1200;
  const contentWidth = maxWidth - sectionPadding - whitePadding * 2;
  
  const titleFontSize = 48;
  const subtitleFontSize = 32;
  const questionFontSize = 28;
  const answerFontSize = 24;
  
  const questionSpacing = 20;
  const titleAnswerSpacing = 45;
  const answerSpacing = 10;
  
  // Calculate layout
  let currentY = whitePadding;
  
  // Title measurement
  const titleHeight = estimateTextHeight(title, contentWidth, titleFontSize, 1.4);
  const titleY = currentY;
  currentY += titleHeight;
  
  // Description measurement
  let descriptionY = 0;
  let descriptionHeight = 0;
  let titleSubtitleSpacing = 0;
  let subtitleQuestionSpacing = 0;
  
  if (description) {
    descriptionHeight = estimateTextHeight(description, contentWidth, subtitleFontSize, 1.4);
    
    // Calculate spacing based on title and description heights
    titleSubtitleSpacing = Math.max(30, Math.min(60, titleHeight * 0.5));
    // Reduced spacing: use fixed reasonable value instead of descriptionHeight + 80 (which was too large)
    subtitleQuestionSpacing = 50;
    
    descriptionY = currentY + titleSubtitleSpacing;
    currentY += titleSubtitleSpacing + descriptionHeight;
  }
  
  currentY += (description ? subtitleQuestionSpacing : questionSpacing);
  
  // Calculate questions positions and heights
  const questionLayouts: Array<{
    questionY: number;
    questionHeight: number;
    answersY: number;
    answerHeights: number[];
    totalHeight: number;
  }> = [];
  
  questions.forEach((q, idx) => {
    const questionY = currentY;
    const questionText = `${idx + 1}. ${q.question}`;
    const questionHeight = estimateTextHeight(questionText, contentWidth, questionFontSize, 1.4);
    
    const answersY = questionY + questionHeight + titleAnswerSpacing;
    const answerHeights: number[] = [];
    
    q.answers.forEach(answer => {
      const answerText = `${answer.letter}) ${answer.text}`;
      const answerHeight = estimateTextHeight(answerText, contentWidth, answerFontSize, 1.4);
      answerHeights.push(answerHeight);
    });
    
    const totalAnswersHeight = answerHeights.reduce((sum, h, i) => 
      sum + h + (i < answerHeights.length - 1 ? answerSpacing : 0), 0
    );
    
    const totalHeight = questionHeight + titleAnswerSpacing + totalAnswersHeight;
    
    questionLayouts.push({
      questionY,
      questionHeight,
      answersY,
      answerHeights,
      totalHeight,
    });
    
    currentY += totalHeight + questionSpacing;
  });
  
  const whiteHeight = currentY - questionSpacing + whitePadding;
  const whiteWidth = maxWidth - sectionPadding;
  
  const totalContentWidth = whiteWidth + sectionPadding;
  const totalContentHeight = whiteHeight + sectionPadding;
  
  // 1. Organizer (SectionWrapper equivalent)
  elements.push({
    id: `sq-organizer-${Date.now()}`,
    type: 'organizer',
    x: 0,
    y: 0,
    width: totalContentWidth,
    height: totalContentHeight,
    rotation: 0,
    data: {
      title: title,
    },
  });
  
  // 2. Section (WhiteSectionBox equivalent)
  const sectionX = sectionPadding / 2;
  const sectionY = sectionPadding / 2;
  
  elements.push({
    id: `sq-section-${Date.now()}`,
    type: 'section',
    x: sectionX,
    y: sectionY,
    width: whiteWidth,
    height: whiteHeight,
    rotation: 0,
    data: {},
  });
  
  // 3. Title text
  elements.push({
    id: `sq-title-${Date.now()}`,
    type: 'text',
    x: whitePadding + sectionX,
    y: titleY + sectionY,
    width: contentWidth,
    height: titleHeight,
    rotation: 0,
    data: {
      text: title,
      fontSize: titleFontSize,
      fontFamily: FONTS.FAMILY,
      fontWeight: FONTS.WEIGHTS.SEMI_BOLD,
      fill: COLORS.BLACK,
      align: direction === 'rtl' ? 'right' : 'left',
    },
  });

  // 4. Description text
  if (description) {
    elements.push({
      id: `sq-desc-${Date.now()}`,
      type: 'text',
      x: whitePadding + sectionX,
      y: descriptionY + sectionY,
      width: contentWidth,
      height: descriptionHeight,
      rotation: 0,
      data: {
        text: description,
        fontSize: subtitleFontSize,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.REGULAR,
        fill: COLORS.BLACK,
        align: direction === 'rtl' ? 'right' : 'left',
      },
    });
  }
  
  // 5. Questions and answers
  questions.forEach((question, qIndex) => {
    const layout = questionLayouts[qIndex];
    const questionText = `${qIndex + 1}. ${question.question}`;
    
    // Question text
    elements.push({
      id: `sq-question-${qIndex}-${Date.now()}`,
      type: 'text',
      x: whitePadding + sectionX,
      y: layout.questionY + sectionY,
      width: contentWidth,
      height: layout.questionHeight,
      rotation: 0,
      data: {
        text: questionText,
        fontSize: questionFontSize,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.SEMI_BOLD,
        fill: COLORS.BLACK,
        align: direction === 'rtl' ? 'right' : 'left',
      },
    });

    // Answers
    question.answers.forEach((answer, aIndex) => {
      const previousHeights = layout.answerHeights.slice(0, aIndex);
      const yOffset = previousHeights.reduce((sum, h, i) => 
        sum + h + answerSpacing, 0
      );
      const answerText = `${answer.letter}) ${answer.text}`;
      
      elements.push({
        id: `sq-answer-${qIndex}-${aIndex}-${Date.now()}`,
        type: 'text',
        x: whitePadding + sectionX,
        y: layout.answersY + yOffset + sectionY,
        width: contentWidth,
        height: layout.answerHeights[aIndex],
        rotation: 0,
        data: {
          text: answerText,
          fontSize: answerFontSize,
          fontFamily: FONTS.FAMILY,
          fontWeight: FONTS.WEIGHTS.REGULAR,
          fill: COLORS.BLACK,
          align: direction === 'rtl' ? 'right' : 'left',
        },
      });
    });
  });

  return elements;
}

/**
 * Converts Five Whys data to editable elements
 * Matches the visual structure from FiveWhysRenderer:
 * - Organizer (SectionWrapper) with title
 * - White section box containing columns
 * - Each column has a header sticky (light gray) and question sticky notes (yellow)
 * - Dynamic heights for all sticky notes
 */
export function convertFiveWhysToElements(data: FiveWhysData): EditableElement[] {
  const elements: EditableElement[] = [];
  const { problemCategories, dir } = data;
  
  if (!problemCategories || problemCategories.length === 0) {
    return elements;
  }

  // Determine text direction
  const direction = dir || getTextDirection(problemCategories[0]?.title || '');
  const title = direction === 'rtl' ? 'تحليل الأسباب الخمسة' : 'Five Whys';
  
  // Layout configuration matching FiveWhysRenderer
  const sectionPadding = 40;
  const whitePadding = 40;
  const columnSpacing = 60;
  const columnWidth = 220;
  const stickySpacing = 20;
  
  const questionNumbers = ["one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
  
  // Calculate dimensions
  const totalColumns = problemCategories.length;
  const whiteWidth = totalColumns * columnWidth + (totalColumns - 1) * columnSpacing + whitePadding * 2;
  
  // Calculate actual height based on tallest column
  let maxColumnHeight = 0;
  const columnHeights: number[] = [];
  const columnStickyPositions: Array<Array<{ y: number; height: number; text: string; isHeader: boolean }>> = [];
  
  problemCategories.forEach((category, catIndex) => {
    const stickyPositions: Array<{ y: number; height: number; text: string; isHeader: boolean }> = [];
    
    // Header height
    const headerHeight = calculateStickyHeight(category.title, columnWidth, 20, SPACING.STICKY_PADDING);
    let columnHeight = whitePadding + headerHeight;
    
    stickyPositions.push({
      y: whitePadding,
      height: headerHeight,
      text: category.title,
      isHeader: true,
    });
    
    // Add each question sticky height
    category.questions.forEach((item, idx) => {
      const questionNumber = questionNumbers[idx] || `${idx + 1}`;
      const stickyText = `Question ${questionNumber}\n${item.question}?\n\nAnswer\n${item.answer}`;
      const stickyHeight = calculateStickyHeight(stickyText, columnWidth, 16, SPACING.STICKY_PADDING);
      
      const stickyY = columnHeight + stickySpacing;
      stickyPositions.push({
        y: stickyY,
        height: stickyHeight,
        text: stickyText,
        isHeader: false,
      });
      
      columnHeight += stickySpacing + stickyHeight;
    });
    
    columnHeight += whitePadding; // Bottom padding
    columnHeights.push(columnHeight);
    maxColumnHeight = Math.max(maxColumnHeight, columnHeight);
    columnStickyPositions.push(stickyPositions);
  });
  
  const whiteHeight = maxColumnHeight;
  
  const totalContentWidth = whiteWidth + sectionPadding * 2;
  const totalContentHeight = whiteHeight + sectionPadding * 2;
  
  // 1. Organizer (SectionWrapper equivalent)
  elements.push({
    id: `fw-organizer-${Date.now()}`,
    type: 'organizer',
    x: 0,
    y: 0,
    width: totalContentWidth,
    height: totalContentHeight,
    rotation: 0,
    data: {
      title: title,
    },
  });
  
  // 2. Section (WhiteSectionBox equivalent)
  const sectionX = sectionPadding;
  const sectionY = sectionPadding;
  
  elements.push({
    id: `fw-section-${Date.now()}`,
    type: 'section',
    x: sectionX,
    y: sectionY,
    width: whiteWidth,
    height: whiteHeight,
    rotation: 0,
    data: {},
  });
  
  // 3. Create columns with sticky notes
  problemCategories.forEach((category, catIndex) => {
    const columnX = sectionX + whitePadding + catIndex * (columnWidth + columnSpacing);
    const stickyPositions = columnStickyPositions[catIndex];
    
    stickyPositions.forEach((pos, stickyIndex) => {
      elements.push({
        id: `fw-sticky-${catIndex}-${stickyIndex}-${Date.now()}`,
        type: 'sticky',
        x: columnX,
        y: pos.y + sectionY,
        width: columnWidth,
        height: pos.height,
        rotation: 0,
        data: {
          text: pos.text,
          backgroundColor: pos.isHeader ? '#E8E8E8' : COLORS.YELLOW,
          fontSize: pos.isHeader ? 20 : 16,
          fontFamily: FONTS.FAMILY,
          fill: COLORS.BLACK,
          align: direction === 'rtl' ? 'right' : 'left',
          padding: SPACING.STICKY_PADDING,
          shadowColor: SHADOWS.STICKY_NOTE.color,
          shadowBlur: SHADOWS.STICKY_NOTE.blur,
          shadowOffsetX: SHADOWS.STICKY_NOTE.offset.x,
          shadowOffsetY: SHADOWS.STICKY_NOTE.offset.y,
          shadowOpacity: SHADOWS.STICKY_NOTE.opacity,
          cornerRadius: SIZES.STICKY_NOTE_CORNER_RADIUS,
        },
      });
    });
  });
  
  return elements;
}

/**
 * Converts Persona data to editable elements
 * Matches the visual structure from PersonaRenderer:
 * - Basic Information Section with orange background, avatar, name, info list, bio, and personality section
 * - Sticky Sections area with orange background and white section boxes containing sticky notes
 */
export function convertPersonaToElements(data: PersonaData): EditableElement[] {
  const elements: EditableElement[] = [];
  const { 
    name, 
    age, 
    position, 
    location, 
    infoText = [], 
    bioText, 
    gender = 'male', 
    personalityProgress = [],
    stickySections = [],
    dir 
  } = data;
  
  const direction = dir || getTextDirection(bioText || '');
  
  // Layout configuration matching PersonaRenderer
  const PADDING = 100;
  const AVATAR_WIDTH = 600;
  const BASIC_INFO_WIDTH = 4018;
  const BASIC_INFO_HEIGHT = 2080;
  const BASIC_INFO_LEFT_WIDTH = BASIC_INFO_WIDTH * 0.55;
  const BASIC_INFO_RIGHT_WIDTH = BASIC_INFO_WIDTH - BASIC_INFO_LEFT_WIDTH - 2 * PADDING;
  
  const stickyColors = [COLORS.GREEN, COLORS.RED, COLORS.BLUE, COLORS.YELLOW];
  
  // Calculate sticky sections layout
  const baseWidth = 3200;
  const wrapperPadding = 100;
  const betweenSpacing = 60;
  const stickyPadding = 40;
  
  let stickySectionsWidth = 0;
  let stickySectionsHeight = 0;
  const stickySectionLayouts: Array<{ x: number; y: number; sectionIndex: number; height: number }> = [];
  
  if (stickySections.length > 0) {
    let currentRowHeight = 0;
    let currentY = wrapperPadding + 80; // Account for title
    let currentX = wrapperPadding;
    
    stickySections.forEach((section, idx) => {
      // Calculate section size with dynamic sticky heights
      const headerHeight = 80;
      const subtitleHeight = section.subtitle ? 60 : 0;
      const stickiesPerRow = 3;
      const stickyWidth = 280;
      const stickySpacing = 20;
      
      // Pre-calculate sticky positions and heights
      let sectionHeight = headerHeight + subtitleHeight + stickyPadding * 2;
      let stickyY = stickyPadding;
      let stickyX = stickyPadding;
      let rowMaxHeight = 0;
      let stickyCount = 0;
      
      section.notes.forEach(noteLines => {
        if (stickyCount > 0 && stickyCount % stickiesPerRow === 0) {
          sectionHeight += rowMaxHeight + stickySpacing;
          stickyY += rowMaxHeight + stickySpacing;
          stickyX = stickyPadding;
          rowMaxHeight = 0;
        }
        
        const noteText = noteLines.join('\n');
        const stickyHeight = calculateStickyHeight(noteText, stickyWidth, 24, SPACING.STICKY_PADDING);
        rowMaxHeight = Math.max(rowMaxHeight, stickyHeight);
        stickyCount++;
      });
      
      sectionHeight += rowMaxHeight; // Add last row
      const sectionWidth = 1000;
      
      if (currentX + sectionWidth > baseWidth) {
        currentX = wrapperPadding;
        currentY += currentRowHeight + betweenSpacing;
        currentRowHeight = 0;
      }
      
      stickySectionLayouts.push({
        x: currentX,
        y: currentY,
        sectionIndex: idx,
        height: sectionHeight,
      });
      
      currentX += sectionWidth + betweenSpacing;
      currentRowHeight = Math.max(currentRowHeight, sectionHeight);
    });
    
    stickySectionsWidth = baseWidth + wrapperPadding * 2;
    stickySectionsHeight = currentY + currentRowHeight + wrapperPadding;
  }
  
  // 1. Basic Information Section - Orange background
  elements.push({
    id: `persona-basic-bg-${Date.now()}`,
    type: 'shape',
    x: 0,
    y: 0,
    width: BASIC_INFO_WIDTH,
    height: BASIC_INFO_HEIGHT,
    rotation: 0,
    data: {
      shapeType: 'rectangle',
      fill: '#FFFBF0',
      backgroundColor: '#FFFBF0',
      stroke: '#FFC470',
      strokeWidth: 2,
      cornerRadius: 0,
    },
  });
  
  // 2. Avatar area - Orange rectangle background
  elements.push({
    id: `persona-avatar-bg-${Date.now()}`,
    type: 'shape',
    x: PADDING,
    y: PADDING,
    width: AVATAR_WIDTH,
    height: AVATAR_WIDTH,
    rotation: 0,
    data: {
      shapeType: 'rectangle',
      fill: COLORS.ORANGE,
      backgroundColor: COLORS.ORANGE,
      stroke: 'transparent',
      strokeWidth: 0,
      cornerRadius: 20,
    },
  });
  
  // 3. Name text
  const nameHeight = estimateTextHeight(name, BASIC_INFO_LEFT_WIDTH - AVATAR_WIDTH - 2 * PADDING, 96, 1.4);
  elements.push({
    id: `persona-name-${Date.now()}`,
    type: 'text',
    x: AVATAR_WIDTH + 2 * PADDING,
    y: PADDING + 30,
    width: BASIC_INFO_LEFT_WIDTH - AVATAR_WIDTH - 2 * PADDING,
    height: nameHeight,
    rotation: 0,
    data: {
      text: name,
      fontSize: 96,
      fontFamily: FONTS.FAMILY,
      fontWeight: FONTS.WEIGHTS.SEMI_BOLD,
      fill: COLORS.BLACK,
      align: direction === 'rtl' ? 'right' : 'left',
    },
  });
  
  // 4. Info list
  const infoItems: string[] = [];
  if (age) infoItems.push(`• Age: ${age}`);
  if (position) infoItems.push(`• Position: ${position}`);
  if (location) infoItems.push(`• Location: ${location}`);
  infoItems.push(...infoText);
  
  infoItems.forEach((text, idx) => {
    const infoY = PADDING + 170 + (idx + 2) * PADDING * 0.8;
    const infoHeight = estimateTextHeight(text, BASIC_INFO_LEFT_WIDTH - AVATAR_WIDTH - 2 * PADDING, 40, 1.4);
    elements.push({
      id: `persona-info-${idx}-${Date.now()}`,
      type: 'text',
      x: AVATAR_WIDTH + 2 * PADDING,
      y: infoY,
      width: BASIC_INFO_LEFT_WIDTH - AVATAR_WIDTH - 2 * PADDING,
      height: infoHeight,
      rotation: 0,
      data: {
        text: text,
        fontSize: 40,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.REGULAR,
        fill: COLORS.BLACK,
        align: direction === 'rtl' ? 'right' : 'left',
      },
    });
  });
  
  // 5. Separator line
  const separatorY = AVATAR_WIDTH + 3 * PADDING;
  elements.push({
    id: `persona-separator-${Date.now()}`,
    type: 'shape',
    x: PADDING,
    y: separatorY,
    width: BASIC_INFO_LEFT_WIDTH - 2 * PADDING,
    height: 1, // Small height for horizontal line
    rotation: 0,
    data: {
      shapeType: 'line',
      stroke: COLORS.ORANGE,
      strokeWidth: 2,
    },
  });
  
  // 6. Bio section
  const bioTitleY = AVATAR_WIDTH + 4 * PADDING;
  elements.push({
    id: `persona-bio-title-${Date.now()}`,
    type: 'text',
    x: PADDING,
    y: bioTitleY,
    width: BASIC_INFO_LEFT_WIDTH - 2 * PADDING,
    height: 64 * 1.4,
    rotation: 0,
    data: {
      text: 'Bio',
      fontSize: 64,
      fontFamily: FONTS.FAMILY,
      fontWeight: FONTS.WEIGHTS.SEMI_BOLD,
      fill: COLORS.BLACK,
      align: direction === 'rtl' ? 'right' : 'left',
    },
  });
  
  if (bioText) {
    const bioTextY = AVATAR_WIDTH + 5 * PADDING;
    const bioTextHeight = estimateTextHeight(bioText, BASIC_INFO_LEFT_WIDTH - 2 * PADDING, 64, 1.4);
    elements.push({
      id: `persona-bio-text-${Date.now()}`,
      type: 'text',
      x: PADDING,
      y: bioTextY,
      width: BASIC_INFO_LEFT_WIDTH - 2 * PADDING,
      height: bioTextHeight,
      rotation: 0,
      data: {
        text: bioText,
        fontSize: 64,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.REGULAR,
        fill: COLORS.BLACK,
        align: direction === 'rtl' ? 'right' : 'left',
      },
    });
  }
  
  // 7. Personality section - Yellow background
  elements.push({
    id: `persona-personality-bg-${Date.now()}`,
    type: 'shape',
    x: BASIC_INFO_LEFT_WIDTH + PADDING,
    y: PADDING,
    width: BASIC_INFO_RIGHT_WIDTH,
    height: BASIC_INFO_HEIGHT - 2 * PADDING,
    rotation: 0,
    data: {
      shapeType: 'rectangle',
      fill: '#FFF7DC',
      backgroundColor: '#FFF7DC',
      stroke: 'transparent',
      strokeWidth: 0,
      cornerRadius: 0,
    },
  });
  
  // Personality title
  elements.push({
    id: `persona-personality-title-${Date.now()}`,
    type: 'text',
    x: BASIC_INFO_LEFT_WIDTH + 2 * PADDING,
    y: 2 * PADDING,
    width: BASIC_INFO_RIGHT_WIDTH - 2 * PADDING,
    height: 64 * 1.4,
    rotation: 0,
    data: {
      text: 'Personality',
      fontSize: 64,
      fontFamily: FONTS.FAMILY,
      fontWeight: FONTS.WEIGHTS.SEMI_BOLD,
      fill: COLORS.BLACK,
      align: direction === 'rtl' ? 'right' : 'left',
    },
  });
  
  // Personality progress bars
  personalityProgress.forEach((item, idx) => {
    const barY = 3 * PADDING + idx * 170;
    
    // Line coordinates matching renderer exactly
    const lineStartX = BASIC_INFO_LEFT_WIDTH + 2 * PADDING;
    const lineEndX = BASIC_INFO_LEFT_WIDTH + BASIC_INFO_RIGHT_WIDTH - PADDING;
    const lineY = barY + 50;
    const lineLength = lineEndX - lineStartX;
    
    // Calculate dot position exactly matching renderer
    // Renderer: fillWidth = (item.score / 100) * (barWidth - 100)
    // where barWidth = BASIC_INFO_RIGHT_WIDTH - 2 * PADDING
    const barWidth = BASIC_INFO_RIGHT_WIDTH - 2 * PADDING;
    const fillWidth = (item.score / 100) * (barWidth - 100);
    // Dot x position: BASIC_INFO_LEFT_WIDTH + 2 * PADDING + fillWidth
    const dotX = lineStartX + fillWidth;
    
    // Text1 (left)
    const text1Height = estimateTextHeight(item.text1, 200, 32, 1.4);
    elements.push({
      id: `persona-progress-text1-${idx}-${Date.now()}`,
      type: 'text',
      x: lineStartX,
      y: barY,
      width: 200,
      height: text1Height,
      rotation: 0,
      data: {
        text: item.text1,
        fontSize: 32,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.REGULAR,
        fill: COLORS.BLACK,
        align: direction === 'rtl' ? 'right' : 'left',
      },
    });
    
    // Text2 (right)
    const text2Height = estimateTextHeight(item.text2, 150, 32, 1.4);
    elements.push({
      id: `persona-progress-text2-${idx}-${Date.now()}`,
      type: 'text',
      x: BASIC_INFO_LEFT_WIDTH + BASIC_INFO_RIGHT_WIDTH - PADDING - 150,
      y: barY,
      width: 150,
      height: text2Height,
      rotation: 0,
      data: {
        text: item.text2,
        fontSize: 32,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.REGULAR,
        fill: COLORS.BLACK,
        align: direction === 'rtl' ? 'right' : 'left',
      },
    });
    
    // Gray line - spans from lineStartX to lineEndX
    elements.push({
      id: `persona-progress-line-${idx}-${Date.now()}`,
      type: 'shape',
      x: lineStartX,
      y: lineY,
      width: lineLength,
      height: 1, // Small height for horizontal line
      rotation: 0,
      data: {
        shapeType: 'line',
        stroke: COLORS.GRAY,
        strokeWidth: 2,
      },
    });
    
    // Black indicator dot (circle) - matching renderer's Rect position
    // Renderer uses: x={BASIC_INFO_LEFT_WIDTH + 2 * PADDING + fillWidth}, y={barY + 40}
    // This is the left edge of a 20x20 rounded rectangle
    // For a circle, we need to center it at that position
    const dotY = barY + 40;
    // Circle center should be at (dotX + 10, dotY + 10) since dotX is the left edge
    // But Konva circles are positioned by center, so we position the element at (dotX, dotY)
    // and the circle will be centered within the 20x20 bounds
    elements.push({
      id: `persona-progress-dot-${idx}-${Date.now()}`,
      type: 'shape',
      x: dotX, // Left edge position (matching renderer's Rect x)
      y: dotY, // Top edge position (matching renderer's Rect y)
      width: 20,
      height: 20,
      rotation: 0,
      data: {
        shapeType: 'circle',
        fill: COLORS.BLACK,
        backgroundColor: COLORS.BLACK,
        stroke: 'transparent',
        strokeWidth: 0,
        cornerRadius: 10, // Make it a perfect circle (radius = width/2)
      },
    });
  });
  
  // 8. Sticky Sections area
  if (stickySections.length > 0) {
    // Orange background for sticky sections
    elements.push({
      id: `persona-sticky-bg-${Date.now()}`,
      type: 'shape',
      x: BASIC_INFO_WIDTH + 2 * PADDING,
      y: 0,
      width: stickySectionsWidth,
      height: stickySectionsHeight,
      rotation: 0,
      data: {
        shapeType: 'rectangle',
        fill: '#FFFBF0',
        backgroundColor: '#FFFBF0',
        stroke: '#FFC470',
        strokeWidth: 2,
        cornerRadius: 0,
      },
    });
    
    // "More about {name}" title
    const moreAboutText = `More about ${name}`;
    const moreAboutHeight = estimateTextHeight(moreAboutText, stickySectionsWidth - 2 * wrapperPadding, 64, 1.4);
    elements.push({
      id: `persona-more-about-${Date.now()}`,
      type: 'text',
      x: BASIC_INFO_WIDTH + 2 * PADDING + wrapperPadding,
      y: 50,
      width: stickySectionsWidth - 2 * wrapperPadding,
      height: moreAboutHeight,
      rotation: 0,
      data: {
        text: moreAboutText,
        fontSize: 64,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.SEMI_BOLD,
        fill: COLORS.BLACK,
        align: direction === 'rtl' ? 'right' : 'left',
      },
    });
    
    // Sticky sections
    stickySectionLayouts.forEach((layout) => {
      const section = stickySections[layout.sectionIndex];
      const sectionColor = stickyColors[layout.sectionIndex % stickyColors.length];
      const sectionWidth = 1000;
      const sectionX = BASIC_INFO_WIDTH + 2 * PADDING + layout.x;
      const sectionY = layout.y;
      
      // White section background
      elements.push({
        id: `persona-sticky-section-bg-${layout.sectionIndex}-${Date.now()}`,
        type: 'shape',
        x: sectionX,
        y: sectionY,
        width: sectionWidth,
        height: layout.height,
        rotation: 0,
        data: {
          shapeType: 'rectangle',
          fill: '#FFFFFF',
          backgroundColor: '#FFFFFF',
          stroke: '#E0E0E0',
          strokeWidth: 1,
          cornerRadius: 8,
        },
      });
      
      // Section title
      const sectionTitleHeight = estimateTextHeight(section.title, sectionWidth - stickyPadding * 2, 48, 1.4);
      elements.push({
        id: `persona-sticky-section-title-${layout.sectionIndex}-${Date.now()}`,
        type: 'text',
        x: sectionX + stickyPadding,
        y: sectionY + stickyPadding,
        width: sectionWidth - stickyPadding * 2,
        height: sectionTitleHeight,
        rotation: 0,
        data: {
          text: section.title,
          fontSize: 48,
          fontFamily: FONTS.FAMILY,
          fontWeight: FONTS.WEIGHTS.SEMI_BOLD,
          fill: COLORS.BLACK,
          align: direction === 'rtl' ? 'right' : 'left',
        },
      });
      
      // Section subtitle
      if (section.subtitle) {
        const subtitleY = sectionY + 80;
        const subtitleHeight = estimateTextHeight(section.subtitle, sectionWidth - stickyPadding * 2, 32, 1.4);
        elements.push({
          id: `persona-sticky-section-subtitle-${layout.sectionIndex}-${Date.now()}`,
          type: 'text',
          x: sectionX + stickyPadding,
          y: subtitleY,
          width: sectionWidth - stickyPadding * 2,
          height: subtitleHeight,
          rotation: 0,
          data: {
            text: section.subtitle,
            fontSize: 32,
            fontFamily: FONTS.FAMILY,
            fontWeight: FONTS.WEIGHTS.REGULAR,
            fill: COLORS.DARK_GRAY,
            align: direction === 'rtl' ? 'right' : 'left',
          },
        });
      }
      
      // Sticky notes in 3-column grid
      const stickiesPerRow = 3;
      const stickyWidth = 280;
      const stickySpacing = 20;
      let stickyY = sectionY + 80 + (section.subtitle ? 60 : 0) + stickyPadding;
      let stickyX = sectionX + stickyPadding;
      let rowMaxHeight = 0;
      let stickyCount = 0;
      
      section.notes.forEach((noteLines, noteIdx) => {
        if (stickyCount > 0 && stickyCount % stickiesPerRow === 0) {
          stickyY += rowMaxHeight + stickySpacing;
          stickyX = sectionX + stickyPadding;
          rowMaxHeight = 0;
        }
        
        const noteText = noteLines.join('\n');
        const stickyHeight = calculateStickyHeight(noteText, stickyWidth, 24, SPACING.STICKY_PADDING);
        
        elements.push({
          id: `persona-sticky-note-${layout.sectionIndex}-${noteIdx}-${Date.now()}`,
          type: 'sticky',
          x: stickyX,
          y: stickyY,
          width: stickyWidth,
          height: stickyHeight,
          rotation: 0,
          data: {
            text: noteText,
            backgroundColor: sectionColor,
            fontSize: 24,
            fontFamily: FONTS.FAMILY,
            fill: COLORS.BLACK,
            align: direction === 'rtl' ? 'right' : 'left',
            padding: SPACING.STICKY_PADDING,
            shadowColor: SHADOWS.STICKY_NOTE.color,
            shadowBlur: SHADOWS.STICKY_NOTE.blur,
            shadowOffsetX: SHADOWS.STICKY_NOTE.offset.x,
            shadowOffsetY: SHADOWS.STICKY_NOTE.offset.y,
            shadowOpacity: SHADOWS.STICKY_NOTE.opacity,
            cornerRadius: SIZES.STICKY_NOTE_CORNER_RADIUS,
          },
        });
        
        stickyX += stickyWidth + stickySpacing;
        rowMaxHeight = Math.max(rowMaxHeight, stickyHeight);
        stickyCount++;
      });
    });
  }
  
  return elements;
}

/**
 * Converts Journey Map data to editable elements
 * Matches the visual structure from JourneyMapRenderer:
 * - Organizer (SectionWrapper) with title
 * - White section box containing persona profile and journey grid
 * - Persona profile section with avatar area, name, and goal
 * - Journey grid with phase headers and category rows with sticky notes
 */
export function convertJourneyMapToElements(data: JourneyMapData): EditableElement[] {
  const elements: EditableElement[] = [];
  const { persona, phases, dir } = data;

  
  if (!phases || phases.length === 0) {
    return elements;
  }

  // Determine text direction
  const direction = dir || getTextDirection(persona.profile);
  
  // Layout configuration matching JourneyMapRenderer
  const sectionPadding = 40;
  const spacing = 20;
  const cellSpacing = 40;
  const rowSpacing = 10;
  const contentPadding = 20;
  
  // Persona section
  const personaWidth = 400;
  const JOURNEY_AVATAR_WIDTH = 120;
  const nameYOffset = JOURNEY_AVATAR_WIDTH + 20;
  const goalYOffset = 30;
  const personaHeight = 300;
  
  // Grid section
  const gridStartX = personaWidth + spacing;
  const gridStartY = contentPadding;
  const cellWidth = 240;
  const cellHeight = 200;
  const rowLabelWidth = 150;
  const headerHeight = 60;
  
  // Category configuration
  const categoryColors = [COLORS.YELLOW, COLORS.GREEN, COLORS.ORANGE, COLORS.RED, COLORS.PINK];
  const categoryTypes = ['actions', 'thoughts', 'emotions', 'opportunities'];
  
  // Calculate total dimensions
  const totalGridWidth = rowLabelWidth + cellSpacing + (phases.length * (cellWidth + cellSpacing));
  const totalGridHeight = headerHeight + rowSpacing + (categoryTypes.length * (cellHeight + rowSpacing));
  const contentWidth = personaWidth + spacing + totalGridWidth;
  const contentHeight = Math.max(personaHeight, totalGridHeight);
  
  // 1. Organizer (SectionWrapper equivalent)
  elements.push({
    id: `jm-organizer-${Date.now()}`,
    type: 'organizer',
    x: 0,
    y: 0,
    width: contentWidth,
    height: contentHeight,
    rotation: 0,
    data: {
      title: 'Journey Map',
    },
  });
  
  // 2. Section (WhiteSectionBox equivalent)
  elements.push({
    id: `jm-section-${Date.now()}`,
    type: 'section',
    x: 0,
    y: 0,
    width: contentWidth,
    height: contentHeight,
    rotation: 0,
    data: {},
  });
  
  // 3. Persona Profile Section
  const personaX = contentPadding;
  const personaY = contentPadding;
  
  // Avatar area (orange background rectangle)
  elements.push({
    id: `jm-avatar-bg-${Date.now()}`,
    type: 'shape',
    x: personaX,
    y: personaY,
    width: JOURNEY_AVATAR_WIDTH,
    height: JOURNEY_AVATAR_WIDTH,
    rotation: 0,
    data: {
      shapeType: 'rectangle',
      fill: COLORS.ORANGE,
      backgroundColor: COLORS.ORANGE,
      stroke: 'transparent',
      strokeWidth: 0,
      cornerRadius: 0,
    },
  });
  
  // Persona name
  const personaName = (persona as any).name || persona.profile;
  const nameHeight = estimateTextHeight(personaName, personaWidth - contentPadding * 2, 20, 1.4);
  elements.push({
    id: `jm-persona-name-${Date.now()}`,
    type: 'text',
    x: personaX,
    y: personaY + nameYOffset,
    width: personaWidth - contentPadding * 2,
    height: nameHeight,
    rotation: 0,
    data: {
      text: personaName,
      fontSize: 20,
      fontFamily: FONTS.FAMILY,
      fontWeight: FONTS.WEIGHTS.BOLD,
      fill: COLORS.BLACK,
      align: direction === 'rtl' ? 'right' : 'left',
    },
  });
  
  // Profile/Goal
  if (persona.goal) {
    const goalText = `Profile: ${persona.goal}`;
    const goalHeight = estimateTextHeight(goalText, personaWidth - contentPadding * 2, 14, 1.4);
    elements.push({
      id: `jm-persona-goal-${Date.now()}`,
      type: 'text',
      x: personaX,
      y: personaY + nameYOffset + goalYOffset,
      width: personaWidth - contentPadding * 2,
      height: goalHeight,
      rotation: 0,
      data: {
        text: goalText,
        fontSize: 14,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.REGULAR,
        fill: COLORS.BLACK,
        align: direction === 'rtl' ? 'right' : 'left',
      },
    });
  }
  
  // 4. Journey Grid
  const gridX = contentPadding + gridStartX;
  const gridY = gridStartY;
  
  // Phase Headers
  phases.forEach((phase, phaseIndex) => {
    const phaseX = phaseIndex * (cellWidth + cellSpacing) + rowLabelWidth + cellSpacing;
    
    // Blue header rectangle
    elements.push({
      id: `jm-phase-header-${phaseIndex}-${Date.now()}`,
      type: 'shape',
      x: gridX + phaseX,
      y: gridY,
      width: cellWidth,
      height: headerHeight,
      rotation: 0,
      data: {
        shapeType: 'rectangle',
        fill: COLORS.BLUE,
        backgroundColor: COLORS.BLUE,
        stroke: 'transparent',
        strokeWidth: 0,
        cornerRadius: 8,
      },
    });
    
    // Phase name text (centered)
    const phaseNameHeight = estimateTextHeight(phase.phase, cellWidth - 20, 12, 1.4);
    elements.push({
      id: `jm-phase-name-${phaseIndex}-${Date.now()}`,
      type: 'text',
      x: gridX + phaseX + (cellWidth / 2) - ((cellWidth - 20) / 2),
      y: gridY + (headerHeight / 2) - (phaseNameHeight / 2),
      width: cellWidth - 20,
      height: phaseNameHeight,
      rotation: 0,
      data: {
        text: phase.phase,
        fontSize: 12,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.BOLD,
        fill: COLORS.BLACK,
        align: 'center',
      },
    });
  });
  
  // Category Rows
  categoryTypes.forEach((categoryType, categoryIndex) => {
    const rowY = gridY + headerHeight + rowSpacing + categoryIndex * (cellHeight + rowSpacing);
    const categoryColor = categoryColors[categoryIndex % categoryColors.length];
    const categoryLabel = categoryType.charAt(0).toUpperCase() + categoryType.slice(1);
    
    // Row Label (colored rectangle)
    elements.push({
      id: `jm-row-label-${categoryIndex}-${Date.now()}`,
      type: 'shape',
      x: gridX,
      y: rowY,
      width: rowLabelWidth,
      height: 60,
      rotation: 0,
      data: {
        shapeType: 'rectangle',
        fill: categoryColor,
        backgroundColor: categoryColor,
        stroke: 'transparent',
        strokeWidth: 0,
        cornerRadius: 8,
      },
    });
    
    // Category label text (centered)
    const labelHeight = estimateTextHeight(categoryLabel, rowLabelWidth - 20, 14, 1.4);
    elements.push({
      id: `jm-category-label-${categoryIndex}-${Date.now()}`,
      type: 'text',
      x: gridX + (rowLabelWidth / 2) - ((rowLabelWidth - 20) / 2),
      y: rowY + (60 / 2) - (labelHeight / 2),
      width: rowLabelWidth - 20,
      height: labelHeight,
      rotation: 0,
      data: {
        text: categoryLabel,
        fontSize: 14,
        fontFamily: FONTS.FAMILY,
        fontWeight: FONTS.WEIGHTS.BOLD,
        fill: COLORS.BLACK,
        align: 'center',
      },
    });
    
    // Sticky Notes for each phase
    phases.forEach((phase, phaseIndex) => {
      const cellData = phase[categoryType as keyof typeof phase];
      if (!cellData || !Array.isArray(cellData) || cellData.length === 0) return;
      
      const cellX = phaseIndex * (cellWidth + cellSpacing) + rowLabelWidth + cellSpacing;
      let stickyY = rowY;
      // Create sticky notes for each item in the cell
   
        const stickyHeight = calculateStickyHeight(cellData.join('\n\n'), cellWidth, 12, SPACING.STICKY_PADDING);
        
        elements.push({
          id: `jm-sticky-${categoryIndex}-${phaseIndex}-${0}-${Date.now()}`,
          type: 'sticky',
          x: gridX + cellX,
          y: stickyY,
          width: cellWidth,
          height: stickyHeight,
          rotation: 0,
          data: {
            text: '- ' + cellData.join('\n\n- ') ,
            backgroundColor: categoryColor,
            fontSize: 12,
            fontFamily: FONTS.FAMILY,
            fill: COLORS.BLACK,
            align: direction === 'rtl' ? 'right' : 'left',
            padding: SPACING.STICKY_PADDING,
            shadowColor: SHADOWS.STICKY_NOTE.color,
            shadowBlur: SHADOWS.STICKY_NOTE.blur,
            shadowOffsetX: SHADOWS.STICKY_NOTE.offset.x,
            shadowOffsetY: SHADOWS.STICKY_NOTE.offset.y,
            shadowOpacity: SHADOWS.STICKY_NOTE.opacity,
            cornerRadius: SIZES.STICKY_NOTE_CORNER_RADIUS,
          },
        });
        
        // Stack sticky notes vertically
        stickyY += stickyHeight + 10; // Small spacing between stacked notes

    });
  });
  
  return elements;
}

/**
 * Converts Mind Map data to editable elements
 * Matches the visual structure from MindMapRenderer:
 * - Organizer (SectionWrapper) with title
 * - White section box containing tree layout
 * - Text elements for nodes (not sticky notes)
 * - Connector elements with bezier curves between parent and child nodes
 */
export function convertMindMapToElements(data: MindMapData): EditableElement[] {
  const elements: EditableElement[] = [];
  const { title, nodes, dir } = data;
  
  if (!nodes || nodes.length === 0) {
    return elements;
  }

  // Determine text direction
  const direction = dir || getTextDirection(title);
  
  // Layout configuration matching MindMapRenderer
  const titleFontSize = 28;
  const nodeFontSize = 16;
  const titleFontFamily = 'Caveat, "Segoe Print", "Comic Sans MS", cursive';
  const nodeFontFamily = FONTS.FAMILY;
  const maxWidth = 1400;
  
  let currentY = 0;
  currentY += titleFontSize * 1.4 + SPACING.XLARGE;
  
  const horizontalGap = 250;
  const verticalGap = 50;
  
  // Branch colors matching FigJam
  const branchColors = [
    '#4CAF50',   // Green
    '#EB7425',   // Orange
    '#9C27B0',   // Purple
    '#2196F3',   // Blue
  ];
  
  // Build tree structure
  interface LayoutNode {
    id: string;
    label: string;
    children: LayoutNode[];
    x: number;
    y: number;
    color: string | null;
    depth: number;
    direction: string;
    textWidth?: number;
  }
  
  const buildTree = (): LayoutNode => {
    const rootNode: LayoutNode = {
      id: 'root',
      label: title,
      children: [],
      x: 0,
      y: 0,
      color: null,
      depth: 0,
      direction: 'center',
    };
    
    const convertNode = (node: typeof nodes[0], branchIndex: number, depth: number, parentColor: string): LayoutNode => {
      const branchColor = depth === 1 ? branchColors[branchIndex % branchColors.length] : parentColor;
      const layoutNode: LayoutNode = {
        id: `node-${branchIndex}`,
        label: node.text,
        children: [],
        x: 0,
        y: 0,
        color: branchColor,
        depth,
        direction: 'right',
      };
      
      if (node.children) {
        node.children.forEach((child, childIndex) => {
          layoutNode.children.push({
            id: `node-${branchIndex}-${childIndex}`,
            label: child.text,
            children: [],
            x: 0,
            y: 0,
            color: branchColor,
            depth: depth + 1,
            direction: 'right',
          });
        });
      }
      
      return layoutNode;
    };
    
    nodes.forEach((node, index) => {
      rootNode.children.push(convertNode(node, index, 1, branchColors[index % branchColors.length]));
    });
    
    return rootNode;
  };
  
  // Calculate subtree height
  const calculateSubtreeHeight = (node: LayoutNode): number => {
    if (node.children.length === 0) {
      return 24; // Single node height
    }
    
    let totalHeight = 0;
    node.children.forEach(child => {
      totalHeight += calculateSubtreeHeight(child) + verticalGap;
    });
    return totalHeight;
  };
  
  // Position nodes with radial layout
  const positionNodes = (
    node: LayoutNode,
    startX: number,
    startY: number
  ): void => {
    node.x = startX;
    node.y = startY;
    
    if (node.children.length === 0) return;
    
    if (node.depth === 0) {
      // Root node: distribute main branches around it (half right, half left)
      const halfCount = Math.ceil(node.children.length / 2);
      
      // Calculate total height for right branches
      let rightTotalHeight = 0;
      for (let i = 0; i < halfCount; i++) {
        rightTotalHeight += calculateSubtreeHeight(node.children[i]) + verticalGap;
      }
      
      // Calculate total height for left branches
      let leftTotalHeight = 0;
      for (let i = halfCount; i < node.children.length; i++) {
        leftTotalHeight += calculateSubtreeHeight(node.children[i]) + verticalGap;
      }
      
      // Right branches
      let rightY = startY - rightTotalHeight / 2;
      for (let i = 0; i < halfCount; i++) {
        const child = node.children[i];
        child.direction = 'right';
        const childX = startX + horizontalGap + 100;
        const subtreeHeight = calculateSubtreeHeight(child);
        positionNodes(child, childX, rightY + subtreeHeight / 2);
        rightY += subtreeHeight + verticalGap;
      }
      
      // Left branches
      let leftY = startY - leftTotalHeight / 2;
      for (let i = halfCount; i < node.children.length; i++) {
        const child = node.children[i];
        child.direction = 'left';
        const childX = startX - horizontalGap - 100;
        const subtreeHeight = calculateSubtreeHeight(child);
        positionNodes(child, childX, leftY + subtreeHeight / 2);
        leftY += subtreeHeight + verticalGap;
      }
    } else {
      // Branch nodes: continue in same direction
      let totalHeight = 0;
      node.children.forEach(child => {
        totalHeight += calculateSubtreeHeight(child) + verticalGap;
      });
      
      let currentY = startY - totalHeight / 2;
      
      node.children.forEach((child) => {
        child.direction = node.direction;
        const childX = node.direction === 'right' 
          ? startX + horizontalGap 
          : startX - horizontalGap;
        
        const subtreeHeight = calculateSubtreeHeight(child);
        positionNodes(child, childX, currentY + subtreeHeight / 2);
        currentY += subtreeHeight + verticalGap;
      });
    }
  };
  
  const tree = buildTree();
  const centerX = maxWidth / 2;
  const centerY = currentY + 200;
  positionNodes(tree, centerX, centerY);
  
  // Calculate text widths for all nodes (matching renderer's canvas measurement)
  const calculateTextWidth = (text: string, fontSize: number, fontFamily: string, fontWeight: string): number => {
    // More accurate text width calculation matching renderer
    // The renderer uses canvas.measureText, we approximate with character width
    // For title font (Caveat), characters are wider; for Inter, use standard width
    const isTitleFont = fontFamily.includes('Caveat') || fontFamily.includes('Segoe Print') || fontFamily.includes('Comic Sans');
    const avgCharWidth = isTitleFont ? fontSize * 0.7 : fontSize * 0.6;
    return text.length * avgCharWidth;
  };
  
  const allNodes: LayoutNode[] = [];
  const connections: Array<{ from: LayoutNode; to: LayoutNode }> = [];
  
  const traverse = (node: LayoutNode): void => {
    // Calculate text width
    const font = node.depth === 0 ? titleFontFamily : nodeFontFamily;
    const size = node.depth === 0 ? titleFontSize : nodeFontSize;
    const weight = node.depth === 0 ? '700' : '500';
    node.textWidth = calculateTextWidth(node.label, size, font, weight);
    
    allNodes.push(node);
    node.children.forEach((child) => {
      connections.push({ from: node, to: child });
      traverse(child);
    });
  };
  
  traverse(tree);
  
  // Calculate bounds for background
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  allNodes.forEach(node => {
    minX = Math.min(minX, node.x - 100);
    minY = Math.min(minY, node.y - 50);
    maxX = Math.max(maxX, node.x + 100);
    maxY = Math.max(maxY, node.y + 50);
  });
  
  const padding = 80;
  const contentX = minX - padding;
  const contentY = minY - padding;
  const contentWidth = maxX - minX + padding * 2;
  const contentHeight = maxY - minY + padding * 2;
  
  // Calculate offset to center content in white box
  const offsetX = -contentX;
  const offsetY = -contentY;
  
  // 1. Organizer (SectionWrapper equivalent)
  elements.push({
    id: `mm-organizer-${Date.now()}`,
    type: 'organizer',
    x: 0,
    y: 0,
    width: contentWidth,
    height: contentHeight,
    rotation: 0,
    data: {
      title: 'Mindmapping brainstorming',
    },
  });
  
  // 2. Section (WhiteSectionBox equivalent)
  elements.push({
    id: `mm-section-${Date.now()}`,
    type: 'section',
    x: 0,
    y: 0,
    width: contentWidth,
    height: contentHeight,
    rotation: 0,
    data: {},
  });
  
  // 3. Create text nodes first and store their IDs in a map
  const nodeIdToElementId = new Map<string, string>();
  const timestamp = Date.now();
  
  allNodes.forEach((node) => {
    const font = node.depth === 0 ? titleFontFamily : nodeFontFamily;
    const size = node.depth === 0 ? titleFontSize : nodeFontSize;
    const weight = node.depth === 0 ? FONTS.WEIGHTS.BOLD : FONTS.WEIGHTS.REGULAR;
    const textWidth = node.textWidth || calculateTextWidth(node.label, size, font, weight);
    
    // Adjust coordinates for offset
    const adjustedX = node.x + offsetX;
    const adjustedY = node.y + offsetY;
    
    // Calculate text height
    const textHeight = estimateTextHeight(node.label, textWidth, size, 1.4);
    
    const elementId = `mm-node-${node.id}-${timestamp}`;
    nodeIdToElementId.set(node.id, elementId);
    
    elements.push({
      id: elementId,
      type: 'text',
      x: adjustedX - textWidth / 2,
      y: adjustedY - size / 2,
      width: textWidth,
      height: textHeight,
      rotation: 0,
      data: {
        text: node.label,
        fontSize: size,
        fontFamily: font,
        fontWeight: weight,
        fill: COLORS.BLACK,
        align: 'center',
      },
    });
  });
  
  // 4. Create connectors with proper startElementId and endElementId (render after section/organizer)
  connections.forEach((conn, index) => {
    // Get element IDs for start and end nodes
    const startElementId = nodeIdToElementId.get(conn.from.id) || null;
    const endElementId = nodeIdToElementId.get(conn.to.id) || null;
    
    // Calculate connector start and end points at text edges
    const startTextWidth = conn.from.textWidth || 0;
    const endTextWidth = conn.to.textWidth || 0;
    const connectorPadding = 10;
    
    const isGoingRight = conn.to.x > conn.from.x;
    
    // Start point: right edge if going right, left edge if going left
    const startX = isGoingRight 
      ? conn.from.x + startTextWidth / 2 + connectorPadding
      : conn.from.x - startTextWidth / 2 - connectorPadding;
    const startY = conn.from.y;
    
    // End point: left edge if coming from right, right edge if coming from left
    const endX = isGoingRight
      ? conn.to.x - endTextWidth / 2 - connectorPadding
      : conn.to.x + endTextWidth / 2 + connectorPadding;
    const endY = conn.to.y;
    
    // Create curved bezier path
    const dx = endX - startX;
    const dy = endY - startY;
    
    // Control points extend horizontally first for smooth curves
    const controlDist = Math.abs(dx) * 0.5;
    const cp1x = startX + (dx > 0 ? controlDist : -controlDist);
    const cp1y = startY;
    const cp2x = endX - (dx > 0 ? controlDist : -controlDist);
    const cp2y = endY;
    
    // Adjust coordinates for offset
    const adjustedStartX = startX + offsetX;
    const adjustedStartY = startY + offsetY;
    const adjustedEndX = endX + offsetX;
    const adjustedEndY = endY + offsetY;
    const adjustedCp1X = cp1x + offsetX;
    const adjustedCp1Y = cp1y + offsetY;
    const adjustedCp2X = cp2x + offsetX;
    const adjustedCp2Y = cp2y + offsetY;
    
    // Calculate bounding box for connector
    const minConnX = Math.min(adjustedStartX, adjustedEndX, adjustedCp1X, adjustedCp2X);
    const minConnY = Math.min(adjustedStartY, adjustedEndY, adjustedCp1Y, adjustedCp2Y);
    const maxConnX = Math.max(adjustedStartX, adjustedEndX, adjustedCp1X, adjustedCp2X);
    const maxConnY = Math.max(adjustedStartY, adjustedEndY, adjustedCp1Y, adjustedCp2Y);
    
    const connWidth = Math.max(1, maxConnX - minConnX);
    const connHeight = Math.max(1, maxConnY - minConnY);
    
    elements.push({
      id: `mm-connector-${index}-${timestamp}`,
      type: 'connector',
      x: minConnX,
      y: minConnY,
      width: connWidth,
      height: connHeight,
      rotation: 0,
      data: {
        pathType: 'curved',
        startElementId: startElementId,
        endElementId: endElementId,
        startAnchor: {
          side: isGoingRight ? 'right' : 'left',
          x: adjustedStartX,
          y: adjustedStartY,
          isPrecise: false,
        },
        endAnchor: {
          side: isGoingRight ? 'left' : 'right',
          x: adjustedEndX,
          y: adjustedEndY,
          isPrecise: false,
        },
        controlPoints: [
          { x: adjustedCp1X, y: adjustedCp1Y },
          { x: adjustedCp2X, y: adjustedCp2Y },
        ],
        stroke: conn.to.color || COLORS.BLACK,
        strokeWidth: 'small', // 3px (matches renderer's strokeWidth={3})
        dashEnabled: false,
        arrowStart: 'none',
        arrowEnd: 'none',
        textPosition: 0.5,
        textBackgroundEnabled: false,
      },
    });
  });
  
  return elements;
}

/**
 * Master converter function - routes to appropriate converter based on type
 */
export function convertVisualizationToElements(
  type: string,
  data: any
): EditableElement[] {
  switch (type) {
    case 'howMightWe':
      return convertHowMightWeToElements(data as HowMightWeData);
    case 'problemStatements':
      return convertProblemStatementsToElements(data as ProblemStatementsData);
    case 'interviewQuestions':
      return convertInterviewQuestionsToElements(data as InterviewQuestionsData);
    case 'userStories':
      return convertUserStoriesToElements(data as UserStoriesData);
    case 'surveyQuestions':
      return convertSurveyQuestionsToElements(data as SurveyQuestionsData);
    case 'fiveWhys':
      return convertFiveWhysToElements(data as FiveWhysData);
    case 'persona':
      return convertPersonaToElements(data as PersonaData);
    case 'journeyMap':
      return convertJourneyMapToElements(data as JourneyMapData);
    case 'mindMap':
      return convertMindMapToElements(data as MindMapData);
    default:
      return [];
  }
}
